import { usePrice } from '../../utils/price';
import { useEffect, useMemo, useState, useCallback, useRef } from 'preact/hooks';
import type { CarProfile } from '../../cars/CarProfile';
import { getSession, type SessionStatus } from '../../core/Session';
import { RaceDash, type DashAction } from '../components/RaceDash';
import { carPath } from '../../cars/registry';
import { longestWord } from './Garage';
import { useT } from '../../i18n';
import { useBuy } from '../useBuy';
import { isCarBrowser } from '../../utils/platform';
import { isDemoCar, DEMO_LIMIT_KMH, demoLimitKmh, demoLeftSeconds, demoConsume, capDemoLeft, isAdmin, getAdminToken } from '../../utils/access';
import { spendDemo } from '../../utils/grant';
import { PurchaseQR } from '../components/PurchaseQR';
import { WheelHint, wheelHintWanted } from '../components/WheelHint';
import { loadSettings } from '../../utils/settings';

function telemetryLabel(s: SessionStatus): string {
  if (s.source === 'gps') {
    const g = s.gps;
    if (g.status === 'ok') return `GPS ±${Math.round(g.accuracyM ?? 0)}m`;
    if (g.status === 'waiting') return 'GPS · WAIT';
    if (g.status === 'stale') return 'GPS · HOLD';
    if (g.status === 'denied') return 'GPS DENIED';
    if (g.status === 'unavailable') return 'NO GPS';
    return 'GPS ERR';
  }
  if (s.source === 'can') {
    const r = s.remote;
    return r.status === 'ok' && r.canOk ? `CAN ${r.hz}Hz` : `CAN · ${r.status.toUpperCase()}`;
  }
  return s.telemetryOk ? `SIM · ${s.driveMode === 'auto' ? 'AUTO' : 'MANUAL'}` : 'SIM · HOLD';
}

export function Engine({ profile }: { profile: CarProfile }) {
  const t = useT();
  const session = useMemo(() => getSession(profile), [profile]);
  const [status, setStatus] = useState<SessionStatus>(session.getStatus());
  const price = usePrice();
  const admin = isAdmin();
  // header / sliders / sim pad only for the admin SIM; in any real drive (GPS, CAN) the HUD is the whole screen (Marc)
  // The instruments (log, levels, pills, REC, faders) are an ADMIN's, behind the simAdmin switch, and
  // nowhere else: a driver, on any screen, gets the clean dash. v502 also hid them from the car's
  // browser and Marc took that back at once ('desde el Tesla en modo admin sim se puede, no?'): the
  // switch is exactly how he reads the car from its own screen.
  const chrome = admin && loadSettings().simAdmin && status.source === 'sim';   // the instrumented SIM (log, levels, pills) — the plain SIM is a clean dash for everyone
  // ...except the faders. Two voices can only be judged with the car actually moving, and a Tesla drives
  // on its own GPS, so on a real drive the admin mixer would never be reachable at all (Marc: "ahora solo
  // puedo utilizar el sim admin y quiero probarlo en el tesla"). It folds into a tab instead of taking
  // the screen, so the rule above still holds for the dash itself.
  const mixer = admin && loadSettings().simAdmin && status.source !== 'sim';
  // the fader row STAYS PUT (Marc: "y el volumen se queda fijo asi!"). It used to fold into a tab in
  // the corner, which was the right call when it was a panel that covered the dash and the wrong one
  // now that it is a single row along the bottom edge.
  const [volume, setVolume] = useState(() => loadSettings().volume);
  // HUD full screen (Marc): on entry only the engine START shows (as a pop-up), the lap timer is hidden; both can be shown/hidden
  // Marc: the bottom row (START/STOP + Racelogic) starts OPEN and folds as one with the chevron
  const [showStart, setShowStart] = useState(true);
  const [showLap, setShowLap] = useState(true);
  // units still key the dash (the gauges redraw when the pit box changes them); they are set in the pit box now
  const units = loadSettings().units;
  useEffect(() => { if (!chrome) { setVolume(1); session.setVolume(1); } }, [chrome, session]);   // non-admin: app at full gain, volume is the car's / phone's
  const [busy, setBusy] = useState(false);
  const [loadPct, setLoadPct] = useState(0);
  // the wheel button is the one thing nobody guesses: say it once, the first time the box goes manual
  const [wheelHint, setWheelHint] = useState(false);
  const hintShown = useRef(false);
  const [showLog, setShowLog] = useState(false);
  const [solo, setSolo] = useState<'base' | 'overlay' | 'sim' | 'extra' | null>(null);
  const savedGains = useRef<{ base: number; overlay: number; sim: number; extra: number } | null>(null);
  const [demo, setDemo] = useState(() => isDemoCar(profile.id));
  const [demoLeft, setDemoLeft] = useState(() => demoLeftSeconds(profile.id));
  const [demoPopup, setDemoPopup] = useState(false);
  // THE DEMO HAS TO SAY SO ON THE DASH (2026-10-25). Its pill lived in the header, and the header only
  // exists in the admin SIM: on a real drive the customer got a V12 that would not leave 4th and no word
  // why (Marc, on his own Tesla, with the three engines his account had never bought). The badge is on
  // every dash in demo; the line under it says it once, the first time the box reaches its cap.
  const [gearHeld, setGearHeld] = useState(false);
  const gearHeldShown = useRef(false);
  // in the phone app the engine is bought through Apple's till, right here on the dash
  const buy = useBuy(profile.id, () => { setDemoPopup(false); setDemo(false); session.setSpeedCap(null); session.audio.setMuted(false); });
  const [audioRev, setAudioRev] = useState('');
  const lastTickRef = useRef(0);
  const unreported = useRef(0);   // demo seconds burned since the last report to the server
  const car = isCarBrowser();
  // the Tesla's own screen has the gear on the wheel: no paddles there, unless the wheel never answered
  const noPaddles = car && status.mediaSession === true;
  const hud = profile.hud ?? { accent: '#ff3d1f', accent2: '#ff6a3d', style: 'default' as const };
  // stable identities so RaceDash's canvas effect never tears down/restarts (was the flicker source)
  const getState = useCallback(() => session.state, [session]);
  const getLap = useCallback(() => session.lapTimer.getState(), [session]);
  const getControls = useCallback(() => {
    const st = session.getStatus();
    return { gearbox: st.gearbox, following: session.followingGear() > 0, style: st.driveStyle, resolvedStyle: st.resolvedStyle, lapRunning: st.lapRunning, lapHasData: st.lapHasData, pitCap: st.pitCap ?? null, rawSpeedKmh: st.rawSpeedKmh, wiperMsg: st.wiperMsg ?? null, lightsOn: st.lightsOn ?? null, tires: st.tires ?? null, wet: st.wet ?? null, showStart, showLap };
  }, [session, showStart, showLap]);
  const onDashAction = useCallback((a: DashAction) => {
    // picking a box by hand is also how you tell us to stop copying your car's (and it brings the
    // paddles back); YOUR CAR turns the copying on again
    if (a === 'gearbox:auto') { session.setFollowMyGears(false); session.setGearbox('auto'); }
    else if (a === 'gearbox:manual') { session.setFollowMyGears(false); session.setGearbox('manual'); }
    else if (a === 'gearbox:car') session.setFollowMyGears(true);
    else if (a === 'style:eco') session.setDriveStyle('eco');
    else if (a === 'style:sport') session.setDriveStyle('sport');
    else if (a === 'lap:start') session.lapStart();
    else if (a === 'lap:mark') session.lapMark();
    else if (a === 'lap:stop') session.lapStop();
    else if (a === 'lap:reset') session.lapReset();
    else if (a === 'engine:toggle') void session.ignition();
    else if (a === 'engine:press') session.starterPress();
    else if (a === 'engine:release') session.starterRelease();
    else if (a === 'launch:toggle') session.toggleLaunch();
    else if (a === 'ui:start') setShowStart((v) => !v);
    else if (a === 'ui:lap') setShowLap((v) => !v);
    else if (a === 'ui:bottom') { const open = !(showStart || showLap); setShowStart(open); setShowLap(open); }
    else if (a === 'wet:on') session.showWiper('ONE WIPE');    // Marc: WET → 'WIPER / ONE WIPE', DRY → 'WIPER / OFF'
    else if (a === 'wet:off') session.showWiper('OFF');
    else if (a === 'pit:toggle') { if (!demo) session.setPitLimiter(session.getStatus().pitCap != null ? null : DEMO_LIMIT_KMH); }
    setStatus(session.getStatus());
  }, [session, demo, showStart, showLap]);

  useEffect(() => {
    // show WHICH audio build this browser actually serves (Marc: "no sé si me llega el push")
    // THE APP BUILD, not the audio cache. This pill exists to answer 'did my push arrive?' and it was
    // reading the cache name (roardrive-audio-vNNN), which only moves when the SOUND FILES change: it
    // still read v369 on a v371 dash and Marc reasonably read that as 'the deploy never came'. The
    // constant is baked into the bundle, so an old bundle keeps showing its own old number, which is
    // exactly the honest answer.
    const readRev = () => setAudioRev(typeof __ROAR_BUILD__ !== 'undefined' ? __ROAR_BUILD__ : 'dev');
    readRev();
    const revId = setInterval(readRev, 5000);
    lastTickRef.current = performance.now();
    const id = setInterval(() => {
      const st = session.getStatus();
      setStatus(st);
      const p = st.audio.progress;
      if (p && p.total > 0) setLoadPct(Math.min(1, p.done / p.total));
      else if (st.audio.state !== 'running') setLoadPct(0);   // a second start begins at 0 %, not at the last one's 100 %
      // once per drive, the moment the engine is actually there and the dash is about to be driven
      if (!hintShown.current && st.started && st.audio.state === 'running' && wheelHintWanted()) {
        hintShown.current = true;
        setWheelHint(true);
        setTimeout(() => setWheelHint(false), 9000);
      }
      // server verdict wins once the engine asked for its grant; before that, the local guess
      const d = st.entitlement ? st.entitlement !== 'full' : isDemoCar(profile.id);
      // THE SIMULATOR IS FREE, AND FREE MEANS WITHOUT A CLOCK (Marc's call as CEO). The sim is the shop
      // window: two fingers on a screen, sitting still. Nobody buys ROARDRIVE for that, they buy it for
      // the car they are actually driving — so the two minutes and the pit limiter belong to a REAL
      // drive, where the wish to own it lives. Cutting the sound at a desk only reads as broken, and the
      // pit box has been promising 'SIM · Free' all along.
      const free = !d || st.source === 'sim';
      setDemo(!free);
      if (free) {
        // bought, admin, or the free simulator: full engine, no clock
        session.setSpeedCap(null);
        session.audio.setMuted(false);
        setDemoPopup(false);
        lastTickRef.current = performance.now();   // sim time is never billed to the road demo
        return;
      }
      // DEMO: pit limiter at DEMO_LIMIT_KMH + DEMO_SECONDS of engine time per day,
      // then the engine goes quiet and the buy QR pops up. During the open week the limiter is
      // off (null lifts the gear cap with it) and the budget is five minutes — see access.ts.
      session.setSpeedCap(demoLimitKmh());
      const now = performance.now();
      const dt = Math.min(1, (now - lastTickRef.current) / 1000);
      lastTickRef.current = now;
      let left = st.entitlement === 'none' ? 0 : demoLeftSeconds(profile.id); // 'none' = demo grants used up today
      if (left > 0 && st.engine === 'running' && st.audio.state === 'running') {
        left = demoConsume(profile.id, dt);
        // …and the server keeps the real ledger: report every 15 s, and take its word for what is left.
        // Wiping the browser buys a new device id, not a new two minutes.
        unreported.current += dt;
        if (unreported.current >= 15) {
          const secs = unreported.current;
          unreported.current = 0;
          void spendDemo(profile.id, secs).then((serverLeft) => {
            if (serverLeft != null) { capDemoLeft(profile.id, serverLeft); setDemoLeft(Math.min(left, serverLeft)); }
          });
        }
      }
      setDemoLeft(left);
      const cap = session.drivetrain.gearCap;
      if (cap != null && !gearHeldShown.current && st.engine === 'running' && session.state.gear >= cap) {
        gearHeldShown.current = true;
        setGearHeld(true);
        setTimeout(() => setGearHeld(false), 7000);
      }
      const over = left <= 0;
      session.audio.setMuted(over);
      if (over && st.started) setDemoPopup(true);
    }, 120);
    return () => { clearInterval(id); clearInterval(revId); };
  }, [session, profile.id]);

  const start = async () => {
    setBusy(true);
    await session.startEngine();
    setBusy(false);
  };
  // arrived via the phone app's remote control → start without a tap when the page
  // already has a (sticky) user activation (Chromium autoplay rule)
  useEffect(() => {
    let auto = false;
    try { auto = sessionStorage.getItem('roardrive:autostart') === '1'; sessionStorage.removeItem('roardrive:autostart'); } catch { /* ignore */ }
    const ua = (navigator as Navigator & { userActivation?: { hasBeenActive: boolean } }).userActivation;
    const inApp = /ROARDRIVE-App/.test(navigator.userAgent);
    if (session.getStatus().audio.state === 'off' && (ua?.hasBeenActive || inApp)) {
      // the tap on the car / DEMO / PURCHASE was the gesture: enter directly, no ENTER screen (Marc)
      void start().then(() => { if (auto) session.ignition(); });   // phone remote picked the car → straight to ENG START
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const audio = status.audio;
  const audioLabel =
    audio.state === 'running'
      ? `AUDIO · ${audio.report ? `${audio.report.loopsReal}${audio.report.format ? audio.report.format[0] : ''}` : '…'}${audioRev ? ` · ${audioRev}` : ''}${session.audio.sweepGlitches > 0 ? ` · g${session.audio.sweepGlitches}` : ''}`
      : audio.state === 'starting'
        ? `LOAD ${audio.progress ? Math.round((audio.progress.done / audio.progress.total) * 100) : 0}%`
        : audio.state === 'suspended'
          ? 'AUDIO · TAP'
          : audio.state === 'error'
            ? 'AUDIO ERR'
            : 'AUDIO OFF';
  const audioTone = audio.state === 'running' ? (audio.report && audio.report.loopsProcedural > 0 ? 'pill--warn' : 'pill--ok') : audio.state === 'off' ? '' : 'pill--warn';
  const telTone = status.source === 'gps' ? (status.gps.status === 'ok' ? 'pill--ok' : 'pill--warn') : status.source === 'can' ? (status.remote.status === 'ok' && status.remote.canOk ? 'pill--ok' : 'pill--warn') : status.telemetryOk ? 'pill--ok' : 'pill--warn';

  // SOLO, so a strange noise can be pointed at instead of guessed at (Marc: "si quieres divideme los
  // 4 sliders y asi identifico cual es el q hace ese ruido"). One tap mutes the other three and keeps
  // this one where its own slider is; tapping the same one again puts them all back where they were.
  const soloNames: ('base' | 'overlay' | 'sim' | 'extra')[] = ['base', 'overlay', 'sim', 'extra'];
  const soloLabel = (v: string) =>
    v === 'base' ? (profile.samples.overlay?.baseLabel ?? 'EXH')
      : v === 'overlay' ? (profile.samples.overlay?.label ?? 'REAL')
        : v === 'sim' ? (profile.samples.sim?.label ?? 'SIM')
          : (profile.samples.extra?.label ?? 'EXTRA');
  const soloHas = (v: string) =>
    v === 'base' ? true : v === 'overlay' ? session.audio.hasOverlay : v === 'sim' ? session.audio.hasSim : session.audio.hasExtra;
  const toggleSolo = (v: 'base' | 'overlay' | 'sim' | 'extra') => {
    const g = session.audio.voiceGains;
    if (!solo) savedGains.current = { ...g };
    const s0 = savedGains.current ?? g;
    if (solo === v) { session.audio.setVoiceGains(s0.base, s0.overlay, s0.sim, s0.extra); setSolo(null); return; }
    session.audio.setVoiceGains(v === 'base' ? s0.base : 0, v === 'overlay' ? s0.overlay : 0,
      v === 'sim' ? s0.sim : 0, v === 'extra' ? s0.extra : 0);
    setSolo(v);
  };
  // the faders themselves, shared by the SIM footer and the real-drive tab so they can never drift apart
  const faders = (
    <>
      <label class="volume">
        <span>VOL</span>
        <input type="range" min="0" max="1" step="0.01" value={volume} onInput={(e) => { const v = Number((e.currentTarget as HTMLInputElement).value); setVolume(v); session.setVolume(v); }} />
      </label>
      {session.audio.hasOverlay && (
        <>
          <label class="volume">
            <span>{profile.samples.overlay?.baseLabel ?? 'SIM'}</span>
            <input type="range" min="0" max="1.5" step="0.01" value={session.audio.launchBoost ? 1.5 : session.audio.voiceGains.base} onInput={(e) => session.audio.setVoiceGains(Number((e.currentTarget as HTMLInputElement).value), session.audio.voiceGains.overlay)} />
          </label>
          <label class="volume">
            <span>{profile.samples.overlay?.label ?? 'REAL'}</span>
            <input type="range" min="0" max="1.5" step="0.01" defaultValue={String(session.audio.voiceGains.overlay)} onInput={(e) => session.audio.setVoiceGains(session.audio.voiceGains.base, Number((e.currentTarget as HTMLInputElement).value))} />
          </label>
        </>
      )}
      {/* the simulator's own bank as a third fader: what the mod REPLACED, so the two can be judged
          against each other rather than each against silence (Marc). Testing cars only. */}
      {session.audio.hasSim && (
        <label class="volume">
          <span>{profile.samples.sim?.label ?? 'SIM'}</span>
          <input type="range" min="0" max="1.5" step="0.01" defaultValue={String(session.audio.voiceGains.sim)} onInput={(e) => session.audio.setVoiceGains(session.audio.voiceGains.base, session.audio.voiceGains.overlay, Number((e.currentTarget as HTMLInputElement).value))} />
        </label>
      )}
      {session.audio.hasExtra && (
        <label class="volume">
          <span>{profile.samples.extra?.label ?? 'EXTRA'}</span>
          <input type="range" min="0" max="1.5" step="0.01" defaultValue={String(session.audio.voiceGains.extra)} onInput={(e) => session.audio.setVoiceGains(session.audio.voiceGains.base, session.audio.voiceGains.overlay, session.audio.voiceGains.sim, Number((e.currentTarget as HTMLInputElement).value))} />
        </label>
      )}
      {(session.audio.hasOverlay || session.audio.hasSim || session.audio.hasExtra) && (
        <div class="solos">
          {soloNames.filter(soloHas).map((v) => (
            <button key={v} class={`solos__btn ${solo === v ? 'solos__btn--on' : ''}`} onClick={() => toggleSolo(v)}>
              {soloLabel(v)}
            </button>
          ))}
        </div>
      )}
    </>
  );

  return (
    <main
      class={`dash dash--${hud.style} ${status.lap.circuitId ? 'dash--ontrack' : ''} ${chrome ? '' : 'dash--clean'} ${status.gearbox === 'manual' ? 'dash--manual' : ''} ${noPaddles ? 'dash--nopaddles' : ''} ${status.source === 'sim' ? 'dash--sim' : ''}`}
      style={{ '--car-accent': hud.accent, '--car-accent-2': hud.accent2 } as Record<string, string>}
      onClick={() => void session.audio.resume()}
    >
      {!chrome && <a class="dash__close" href={`/garage/${carPath(profile)}`} aria-label="Close" title="Back to the garage bay" />}
      <div class="dash__rotate" aria-hidden="true"><span>⟳</span>{t('rotatePhone')}</div>
      {demo && !chrome && (() => {
        const time = `${Math.floor(demoLeft / 60)}:${String(Math.floor(demoLeft % 60)).padStart(2, '0')}`;
        const cap = demoLimitKmh();
        const gear = session.drivetrain.gearCap;
        return (
          <div class={`demobadge ${demoLeft <= 15 ? 'demobadge--warn' : ''}`} onClick={(e) => e.stopPropagation()}>
            <span class="demobadge__text">{cap != null && gear != null ? t('demoBadge', { cap, gear, time }) : t('demoBadgeOpen', { time })}</span>
            {/* in the phone app the engine is bought through the store's own till, never a web checkout */}
            <button class="demobadge__buy" disabled={buy.busy} onClick={() => (buy.canBuy ? void buy.buy() : void (window.location.href = `/unlock/${carPath(profile)}`))}>{t('demoBadgeBuy')}</button>
            {gearHeld && cap != null && gear != null && <div class="demobadge__held">{t('demoGearHeld', { cap, gear })}</div>}
          </div>
        );
      })()}
      {chrome && <header class="dash__bar">
        <a class="back" href={`/garage/${carPath(profile)}`}>
          ← Roar<span>Drive</span>
        </a>
        <div class="dash__car">
          <span class="dash__maker">{profile.manufacturer}</span>
          <span class="dash__name">{profile.name}</span>
        </div>
        <div class="dash__status">
          {demo && <span class={`pill pill--demo ${demoLeft <= 15 ? 'pill--warn' : ''}`}>DEMO · {demoLimitKmh() != null ? `PIT LIMITER ${DEMO_LIMIT_KMH}` : 'NO LIMITER'} · {Math.floor(demoLeft / 60)}:{String(Math.floor(demoLeft % 60)).padStart(2, '0')}</span>}
          <button class={`pill pill--btn ${telTone}`} onClick={() => session.toggleTelemetrySource()}>{telemetryLabel(status)}</button>
          <span class={`pill ${audioTone}`}>{audioLabel}</span>
          <button class={`pill pill--btn ${showLog ? 'pill--active' : ''}`} onClick={() => setShowLog(!showLog)}>LOG</button>
        </div>
      </header>}

      <section class="dash__screen">
        {/* On the Tesla's own screen the gear lives on the steering wheel, so the paddles are two strips of
            dash spent on a button the driver already has under their thumb (Marc). Anywhere else — a phone,
            a RaceBox, an OBD dongle — there is no wheel button, so the paddles stay.
            They also stay if the media session did not attach: that is the wheel not answering, and a
            manual box with no way to change gear is worse than a narrow dash. */}
        {status.gearbox === 'manual' && !noPaddles && (
          <>
            <button class="paddle paddle--down" onPointerDown={() => session.shift(-1)} aria-label="Shift down"><span>▼</span><small>DOWN</small></button>
            <button class="paddle paddle--up" onPointerDown={() => session.shift(1)} aria-label="Shift up"><span>▲</span><small>UP</small></button>
          </>
        )}
        <RaceDash key={units} profile={profile} getState={getState} getLap={getLap} getControls={getControls} onAction={onDashAction} accent={hud.accent} accent2={hud.accent2} style={hud.style} />
        {/* the 812 and the SVJ draw their own starter INSIDE the dash, in the shape the car uses:
            the Ferrari's red wheel button and the Lamborghini's flip-up cover. */}
        {status.started && hud.style !== 'cosworth' && hud.style !== 'f812' && hud.style !== 'svj' && (
          <button
            class={`ignbtn ignbtn--${status.engine} ${status.engine === 'running' && session.state.speedKmh > 3 ? 'ignbtn--locked' : ''}`}
            onClick={(e) => { e.stopPropagation(); void session.ignition(); }}
            disabled={status.engine === 'cranking' || status.engine === 'stopping'}
            aria-label={status.engine === 'running' ? 'Engine stop' : 'Engine start'}
            title={status.engine === 'running' && session.state.speedKmh > 3 ? t('engineStopHint') : ''}
          >
            <span class="ignbtn__bezel" />
            <span class="ignbtn__cap">
              <small>ENGINE</small>
              <b>{status.engine === 'off' ? 'START' : status.engine === 'cranking' ? 'START' : status.engine === 'running' ? 'STOP' : 'STOP'}</b>
              <i />
            </span>
          </button>
        )}
      </section>

      {chrome && <footer class={`dash__controls ${status.gearbox === 'manual' ? 'dash__controls--manual' : ''}`}>{faders}</footer>}

      {mixer && (
        <div class="dash__mix dash__mix--open">
          <div class="dash__mixpanel">
            {/* what the engine ACTUALLY loaded, where an admin can see it in the car. Reading this first
                is the lesson that cost two days once: format, how many real loops, and what is missing
                answers 'why do I hear nothing' before any theory does. */}
            <span class="dash__mixdiag">{audioLabel} · {audio.report ? `${audio.report.loopsReal} loops` : 'no report'}
              {audio.report?.missing?.length ? ` · ${audio.report.missing.length} missing` : ''}
              {` · ENG ${String(status.engine).toUpperCase()}`}</span>
            {faders}
          </div>
        </div>
      )}

      {status.source === 'sim' && status.started && (
        // the SIM's pedals, for everyone: hold THROTTLE / BRAKE. The gear buttons belong to the instrumented
        // SIM: the box is automatic, and Marc wants the free one to be two pedals and nothing else.
        <div class="simpad" aria-hidden="true">
          {(() => {
            const keysMode = () => { if (session.getStatus().driveMode === 'auto') session.toggleDriveMode(); };
            const hold = (pedal: number, brake: number) => ({
              onPointerDown: (e: PointerEvent) => { e.preventDefault(); keysMode(); try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* no capture: the pedal still works, leave/up still release it */ } session.setTouchPedals(pedal, brake); },
              onPointerUp: () => session.setTouchPedals(0, 0),
              onPointerCancel: () => session.setTouchPedals(0, 0),
              onPointerLeave: () => session.setTouchPedals(0, 0),
            });
            return (
              <>
                {chrome && (
                  <div class="simpad__col simpad__col--left">
                    <button class="simpad__btn" onPointerDown={(e) => { e.preventDefault(); keysMode(); session.shift(1); }}>›<small>GEAR +</small></button>
                    <button class="simpad__btn" onPointerDown={(e) => { e.preventDefault(); keysMode(); session.shift(-1); }}>‹<small>GEAR −</small></button>
                  </div>
                )}
                <div class="simpad__col simpad__col--right">
                  <button class="simpad__btn simpad__btn--gas" {...hold(1, 0)}>↑<small>THROTTLE</small></button>
                  <button class="simpad__btn simpad__btn--brake" {...hold(0, 1)}>↓<small>BRAKE</small></button>
                </div>
              </>
            );
          })()}
        </div>
      )}
      {status.source === 'sim' && status.started && (
        <div class="simkeys" aria-hidden="true">
          <span><kbd>I</kbd> engine</span><span><kbd>↑</kbd> gas</span><span><kbd>↓</kbd> brake</span><span><kbd>←</kbd> <kbd>→</kbd> gears</span><span><kbd>M</kbd> {status.gearbox === 'auto' ? 'auto' : 'manual'} box</span><span><kbd>K</kbd> {status.driveMode === 'auto' ? 'auto-pilot' : 'keys'}</span>
        </div>
      )}
      {showLog && (
        <aside class="logpanel" onClick={(e) => e.stopPropagation()}>
          <div class="logpanel__head"><span>SESSION LOG</span><button
            onClick={async (e) => {
              const btn = e.currentTarget as HTMLButtonElement;
              // 15 s, and it goes to the server: a Tesla or a phone has nowhere to put a download,
              // and the whole point is to hear what THAT browser rendered (Marc's 'piedrecitas')
              btn.textContent = 'REC 15s…'; btn.disabled = true;
              session.addLog(`audio ctx: ${session.audio.contextInfo}`);
              const cap = await session.audio.debugCapture(15);
              if (cap) {
                session.addLog(`debug capture (${cap.info})`);
                // in 48 KB base64 pieces: the Tesla's browser refused the take as one POST ('Failed to fetch')
                try {
                  const buf = new Uint8Array(await cap.blob.arrayBuffer());
                  const PIECE = 36 * 1024;
                  const total = Math.max(1, Math.ceil(buf.length / PIECE));
                  const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
                  let last: { have?: number; bytes?: number; done?: boolean; mailed?: boolean; key?: string; mailWhy?: string } = {};
                  for (let seq = 0; seq < total; seq++) {
                    const piece = buf.subarray(seq * PIECE, (seq + 1) * PIECE);
                    let bin = '';
                    for (let i = 0; i < piece.length; i += 8192) bin += String.fromCharCode.apply(null, Array.from(piece.subarray(i, i + 8192)));
                    const body = JSON.stringify({ id, seq, total, data: btoa(bin), info: `${session.profile.id} ${cap.info}` });
                    let r: Response | null = null;
                    for (let attempt = 0; attempt < 3 && !r; attempt++) {
                      try {
                        r = await fetch('/api/diag/audio', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAdminToken() ?? ''}` }, body });
                      } catch (e) { session.addLog(`piece ${seq + 1}/${total} try ${attempt + 1}: ${String(e)} online=${navigator.onLine}`); await new Promise((res) => setTimeout(res, 800)); }
                    }
                    if (!r || !r.ok) { session.addLog(`capture upload failed at piece ${seq + 1}/${total}: ${r ? r.status : 'no response'}`); break; }
                    last = await r.json().catch(() => ({}));
                  }
                  if (last.done) session.addLog(`capture uploaded, KEY ${last.key} (${last.bytes} bytes, ${total} pieces)${last.mailed ? ', mailed to you' : `, mail failed: ${last.mailWhy ?? '?'}`}`);
                } catch (e) { session.addLog(`capture upload error: ${String(e)}`); }
                if (!isCarBrowser()) {
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(cap.blob);
                  a.download = `rd-debug-${cap.info.split(' ')[0]}-${Date.now()}.webm`;
                  a.click();
                }
              }
              btn.textContent = 'REC 15s'; btn.disabled = false;
            }}
          >REC 15s</button><button
            title="the idle rung through one source node straight to the output: no graph"
            onClick={() => session.addLog(session.audio.debugRaw('node'))}
          >RAW 1N</button><button
            title="the same file through a plain audio element: no Web Audio at all"
            onClick={() => session.addLog(session.audio.debugRaw('element'))}
          >RAW EL</button><button
            title="move the mixer's output down the chain: 1 straight out, 2 master+comp, 3 all but the pedal stage, 0 full"
            onClick={() => session.addLog(session.audio.debugBypass())}
          >BYP</button><button
            title="the mixer's own automation: 1 frozen, 2 gains only, 3 rates only, 0 normal"
            onClick={() => session.addLog(session.audio.debugMix())}
          >MIX</button><button onClick={() => setShowLog(false)}>✕</button></div>
          <pre>{[...session.log].reverse().join('\n')}</pre>
        </aside>
      )}

      {/* the engine could not get its recordings (entitlement refused, network, 404). It used to fall back
          to a synthesiser and sing, which is how two days went into chasing a "horn" that was the
          fallback itself. Say it, and offer the one thing that fixes it. */}
      {status.audio.state === 'error' && status.audio.error === 'audio-denied' && (
        <div class="demogate" onClick={(e) => e.stopPropagation()}>
          <div class="demogate__card">
            <div class="demogate__head">{t('audioDeniedTitle')}</div>
            <div class="demogate__note">{t('audioDeniedBody')}</div>
            <button class="acctpanel__btn" onClick={() => { void session.refreshGrant().then(() => window.location.reload()); }}>{t('audioDeniedRetry')}</button>
            <a class="demogate__later" href="/">{t('backToEngines')}</a>
          </div>
        </div>
      )}
      {/* the car's browser refused its position: the one silent failure a user cannot read. The pill
          said GPS DENIED in six-point type and a Model 3 owner wrote in that 'the simulator turns on but
          gets no data'. Say what it is and the one thing that fixes it in a Tesla (there are no
          per-site settings there: clearing the browsing data is the reset). */}
      {status.source === 'gps' && status.gps.status === 'denied' && (
        <div class="demogate" onClick={(e) => e.stopPropagation()}>
          <div class="demogate__card">
            <div class="demogate__head">{t('gpsDeniedTitle')}</div>
            <div class="demogate__note">{t('gpsDeniedBody')}</div>
            <button class="acctpanel__btn" onClick={() => window.location.reload()}>{t('gpsDeniedRetry')}</button>
            <a class="demogate__later" href="/">{t('backToEngines')}</a>
          </div>
        </div>
      )}
      {demoPopup && (
        <div class="demogate" onClick={(e) => e.stopPropagation()}>
          <div class="demogate__card">
            <div class="demogate__head">{t('demoOver')}</div>
            <div class="demogate__car">{profile.manufacturer} <b>{profile.name}</b></div>
            <div class="demogate__price">{t('unlockFull', { price })}</div>
            {buy.canBuy
              ? <><button class="acctpanel__btn" disabled={buy.busy || buy.onSale === false} onClick={() => void buy.buy()}>{buy.onSale === false ? t('notInAppYet') : buy.busy ? buy.busyLabel : t('buyInApp', { price: buy.price || price })}</button>
                  {buy.note && <div class="demogate__note">{buy.note}</div>}</>
              : <PurchaseQR profile={profile} size={220} onPaid={() => { void session.refreshGrant().then(() => { setDemoPopup(false); setDemo(false); session.setSpeedCap(null); session.audio.setMuted(false); }); }} />}
            <a class="demogate__later" href="/">{t('backToEngines')}</a>
          </div>
        </div>
      )}

      {wheelHint && <WheelHint onClose={() => setWheelHint(false)} />}
      {/* the engine is a download before it is a sound: ten megabytes over a car's LTE is a few seconds,
          and a screen that says nothing for them reads as broken. This says how far it has got. */}
      {(busy || status.audio.state === 'starting') && (
        <div class="engload">
          <span class="engload__label">{t('loading')}</span>
          <div class="engload__bar"><i style={{ width: `${Math.round(loadPct * 100)}%` }} /></div>
          <span class="engload__pct">{Math.round(loadPct * 100)}%</span>
        </div>
      )}
      {!status.started && (
        <div class="startover">
          <div class="startover__car">{profile.manufacturer} <b>{profile.name}</b><small>{profile.tagline}</small></div>
          <button class="startbtn" style={{ '--len': longestWord(busy ? t('loading') : t('tapToEnter')) } as Record<string, number>} onClick={start} disabled={busy}>{busy ? t('loading') : t('tapToEnter')}</button>

          <div class="startover__note">{t('volumeNote')}{car && <><br />{t('teslaAudioNote')}</>}</div>
          <a class="startover__source" href={`/garage/${carPath(profile)}`}>{t('drivingInput')}: <b>{status.source === 'can' ? t('proHw') : status.source === 'sim' ? 'SIM' : 'GPS'}</b> · {t('change')}</a>
        </div>
      )}
    </main>
  );
}
